import type { ImageFormat } from './image-encoder'
import type { QualityMode } from './quality'
import type { PdfDocument } from './pdf-loader'
import { renderPage, CanvasSizeError } from './pdf-renderer'
import { encodeImage } from './image-encoder'
import { QUALITY_PROFILES, getScale } from './quality'
import { createPageFilename, getBaseName } from './filename'
import { cleanupCanvas } from '../utils/memory'

export type ConversionOptions = {
  format: ImageFormat
  quality: QualityMode
  pages: number[]
}

export type ConversionProgress = {
  completed: number
  total: number
  currentPage: number
}

export type ConversionResult = {
  blobs: { name: string; blob: Blob }[]
  totalSize: number
  format: ImageFormat
  baseName: string
  skippedPages: number[]
}

export type PageErrorAction = 'retry' | 'skip' | 'cancel'

export type PageErrorContext = {
  pageNumber: number
  error: Error
  message: string
}

export type ConversionCallbacks = {
  onProgress?: (progress: ConversionProgress) => void
  shouldCancel?: () => boolean
  onPageError?: (ctx: PageErrorContext) => Promise<PageErrorAction>
}

export class ConversionCancelledError extends Error {
  constructor() {
    super('Conversion cancelled by user')
    this.name = 'ConversionCancelledError'
  }
}

function getPageErrorMessage(err: unknown): string {
  if (err instanceof CanvasSizeError) {
    return `Page is too large for your browser at the selected resolution (${err.width}×${err.height}px). Try Smart HD quality.`
  }
  const msg = err instanceof Error ? err.message : String(err)
  if (msg.toLowerCase().includes('out of memory') || msg.toLowerCase().includes('oom')) {
    return 'Your device doesn\u2019t have enough available memory to convert this page at the selected quality. Try Smart HD or convert fewer pages.'
  }
  return `Page couldn\u2019t be converted: ${msg}`
}

/**
 * Converts selected pages of a PDF to images sequentially.
 *
 * Concurrency = 1 (PRD §26): one canvas at a time to avoid
 * excessive memory usage. Each page is rendered at full target
 * resolution, encoded, then canvas + page references are cleaned
 * up before processing the next page (PRD §27).
 *
 * On per-page failure, calls onPageError callback to let the
 * user choose Retry, Skip, or Cancel (PRD §36).
 */
export async function convertPdf(
  pdf: PdfDocument,
  filename: string,
  options: ConversionOptions,
  callbacks?: ConversionCallbacks,
): Promise<ConversionResult> {
  const { format, quality, pages } = options
  const profile = QUALITY_PROFILES[quality]
  const scale = getScale(profile)
  const baseName = getBaseName(filename)
  const totalPages = pdf.numPages
  const extension = format === 'jpeg' ? 'jpg' : 'png'

  const blobs: { name: string; blob: Blob }[] = []
  const skippedPages: number[] = []
  let totalSize = 0

  if (pages.length === 0) {
    return { blobs, totalSize, format, baseName, skippedPages }
  }

  for (let i = 0; i < pages.length; i++) {
    if (callbacks?.shouldCancel?.()) {
      throw new ConversionCancelledError()
    }

    const pageNumber = pages[i]

    callbacks?.onProgress?.({
      completed: i,
      total: pages.length,
      currentPage: pageNumber,
    })

    let succeeded = false

    while (!succeeded) {
      if (callbacks?.shouldCancel?.()) {
        throw new ConversionCancelledError()
      }

      try {
        const rendered = await renderPage(pdf, pageNumber, scale)

        const blob = await encodeImage(
          rendered.canvas,
          format,
          profile.jpegQuality,
        )

        const name = createPageFilename(
          baseName,
          pageNumber,
          totalPages,
          extension,
        )
        blobs.push({ name, blob })
        totalSize += blob.size

        cleanupCanvas(rendered.canvas)
        succeeded = true
      } catch (err) {
        if (err instanceof ConversionCancelledError) throw err

        const error = err instanceof Error ? err : new Error(String(err))
        const message = getPageErrorMessage(error)

        if (callbacks?.onPageError) {
          const action = await callbacks.onPageError({
            pageNumber,
            error,
            message,
          })

          if (action === 'cancel') {
            throw new ConversionCancelledError()
          }
          if (action === 'skip') {
            skippedPages.push(pageNumber)
            break
          }
          if (action === 'retry') {
            continue
          }
        } else {
          throw error
        }
      }
    }

    await new Promise((resolve) => setTimeout(resolve, 0))
  }

  callbacks?.onProgress?.({
    completed: pages.length,
    total: pages.length,
    currentPage: pages[pages.length - 1] ?? 0,
  })

  return { blobs, totalSize, format, baseName, skippedPages }
}
